import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function SEOHead() {
  useEffect(() => {
    document.title = "Start Raising Funds Now | Medical Fundraiser | DonatePe";
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Start your medical fundraiser on DonatePe instantly. Get help in 3 quick steps — create, share, and receive donations from caring people across India.");
    setMeta("keywords", "start medical fundraiser India, raise funds now, DonatePe fundraiser, create fundraiser instantly, medical crowdfunding steps");
    setMeta("robots", "index, follow");
    setMeta("og:title", "Start Raising Funds Now | DonatePe", true);
    setMeta("og:description", "Create your fundraiser instantly on DonatePe and begin receiving support within minutes.", true);
    setMeta("og:type", "website", true);

    let ld = document.getElementById("ld-json-start");
    if (!ld) { ld = document.createElement("script"); ld.id = "ld-json-start"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Start Raising Funds Now | DonatePe",
      description: "Start your medical fundraiser on DonatePe instantly.",
      url: "https://donatePe.in/start-fundraiser",
      provider: { "@type": "Organization", name: "DonatePe", url: "https://donatePe.in" },
    });
  }, []);
  return null;
}

const STEPS = [
  {
    num: "1",
    title: "Start Your Fundraiser",
    desc: "Create your fundraiser in minutes. No paperwork, no delays.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    ),
  },
  {
    num: "2",
    title: "Share With Your Network",
    desc: "Share your fundraiser link on WhatsApp, Instagram, Facebook and with friends & family.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
      </svg>
    ),
  },
  {
    num: "3",
    title: "Receive Donations",
    desc: "Start receiving donations instantly from caring people across the country. Withdraw anytime.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
  },
];

export default function StartFundraiser() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // Check for logged in user
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (err) {
        setUser(null);
      }
    }
  }, []);

  // Handle Start Fundraiser click based on user login status
  const handleStartFundraiser = () => {
    if (user) {
      // Logged in user - go to detailsScreen
      navigate("/detailsScreen", {
        state: {
          userId: user.id || user.userId
        }
      });
    } else {
      // Non-logged in user - go to sign in page first, then start fundraiser
      // Store intent to start fundraiser after login
      sessionStorage.setItem("redirectAfterLogin", "/start-fundraiser");
      navigate("/signin");
    }
  };

  // Handle Get a Callback
  const handleGetCallback = () => {
    navigate("/contact");
  };

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-white" style={{ fontFamily: "DM Sans, sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet"/>

        {/* ── HERO BANNER ── */}
        <section
          className="relative overflow-hidden py-20 px-5 text-center"
          style={{ background: "linear-gradient(135deg, #E0F8F7 0%, #F0FDFC 60%, #ffffff 100%)" }}
          aria-label="Page hero"
        >
          <div
            className="absolute top-[-100px] right-[-60px] w-80 h-80 rounded-full opacity-50 pointer-events-none"
            style={{ background: "radial-gradient(circle, #B2EEEC 0%, transparent 70%)" }}
          />
          <div
            className="absolute bottom-[-80px] left-[-40px] w-64 h-64 rounded-full opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, #B2EEEC 0%, transparent 70%)" }}
          />
          <div className="max-w-3xl mx-auto relative z-10">
            <div
              className="inline-flex items-center gap-2 bg-white border text-xs font-bold px-4 py-1.5 rounded-full mb-5 shadow-sm"
              style={{ borderColor: "#B2EEEC", color: "#006B68" }}
            >
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: "#00B7B3" }} />
              Quick & Easy · No Fees · 24/7 Support
            </div>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight mb-5"
              style={{ fontFamily: "Sora, sans-serif" }}
            >
              Start <span style={{ color: "#00B7B3" }}>Raising Funds</span> Now
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              Don't wait in stressful situations. Create your fundraiser instantly on DonatePe and begin receiving support within minutes. Share your story, connect with donors, and take the first step toward covering your medical expenses today.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={handleStartFundraiser}
                className="text-white font-bold px-8 py-4 rounded-full text-base transition-all hover:-translate-y-0.5 transform"
                style={{ background: "#00B7B3", fontFamily: "Sora, sans-serif", boxShadow: "0 8px 24px rgba(0,183,179,0.28)" }}
              >
                Start a Free Fundraiser
              </button>
              <button
                onClick={handleGetCallback}
                className="bg-white font-bold border-2 px-8 py-4 rounded-full text-base transition-all hover:bg-gray-50"
                style={{ color: "#00B7B3", borderColor: "#00B7B3", fontFamily: "Sora, sans-serif" }}
              >
                Get a Callback
              </button>
            </div>
          </div>
        </section>

        {/* ── 3 STEPS ── */}
        <section className="bg-gray-50 py-20 px-5" aria-labelledby="steps-heading">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#00B7B3" }}>Simple Process</p>
              <h2
                id="steps-heading"
                className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-3"
                style={{ fontFamily: "Sora, sans-serif" }}
              >
                Get Help in 3 Quick Steps
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Raising funds is simple and fast. Start your fundraiser, share it with your network, and receive donations from caring people across the country. No complex process, no delays — just quick help when you need it the most.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {STEPS.map((step, i) => (
                <article
                  key={step.num}
                  className="bg-white rounded-2xl border border-gray-100 p-8 text-center relative transition-all duration-200 hover:-translate-y-1"
                  style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.04)" }}
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                    style={{ background: "#E0F8F7", color: "#00B7B3" }}
                  >
                    {step.icon}
                  </div>
                  <div
                    className="w-7 h-7 rounded-full text-white text-xs font-extrabold flex items-center justify-center mx-auto mb-4"
                    style={{ background: "#00B7B3", fontFamily: "Sora, sans-serif" }}
                  >
                    {step.num}
                  </div>
                  <h3
                    className="font-bold text-gray-900 mb-3 text-lg"
                    style={{ fontFamily: "Sora, sans-serif" }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Turn Your Need Into Support Section ── */}
        <section className="py-20 px-5 bg-white" aria-labelledby="support-heading">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-14 items-center">
              <div>
                <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#00B7B3" }}>Turn Need Into Support</p>
                <h2
                  id="support-heading"
                  className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-5"
                  style={{ fontFamily: "Sora, sans-serif" }}
                >
                  Turn Your Need Into Support
                </h2>
                <p className="text-gray-500 text-base leading-relaxed">
                  Your situation matters. When you share your story, people come forward to help. DonatePe connects you with kind donors who are ready to support your medical journey and reduce your financial burden.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="assets/support.jpg"
                    alt="Doctor and patient discussing treatment"
                    className="w-full h-[360px] object-cover"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute -bottom-4 -right-4 w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ background: "#00B7B3" }}
                >
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Begin Your Fundraising Journey Section ── */}
        <section className="py-20 px-5 bg-gray-50" aria-labelledby="journey-heading">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-14 items-center">
              <div className="relative md:order-1">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="assets/fundraising.jpg"
                    alt="Family supporting patient in hospital"
                    className="w-full h-[360px] object-cover"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute -bottom-4 -left-4 w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ background: "#00B7B3" }}
                >
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                </div>
              </div>
              <div className="md:order-2">
                <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#00B7B3" }}>Begin Journey</p>
                <h2
                  id="journey-heading"
                  className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-5"
                  style={{ fontFamily: "Sora, sans-serif" }}
                >
                  Begin Your Fundraising Journey
                </h2>
                <p className="text-gray-500 text-base leading-relaxed">
                  Every journey starts with one step. Create your fundraiser, spread the word, and watch the support grow. With DonatePe, you're not alone — we're here to help you every step of the way.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── BOTTOM CTA STRIP ── */}
        <section
          className="py-16 px-5 text-center"
          style={{ background: "linear-gradient(135deg, #004D4B, #006B68)" }}
          aria-label="Call to action"
        >
          <div className="max-w-xl mx-auto">
            <h2
              className="text-2xl md:text-3xl font-extrabold text-white mb-4 tracking-tight"
              style={{ fontFamily: "Sora, sans-serif" }}
            >
              You Are Not Alone
            </h2>
            <p className="mb-8" style={{ color: "rgba(255,255,255,0.7)" }}>
              Thousands of donors across India are ready to help. Take the first step today.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={handleStartFundraiser}
                className="text-white font-bold px-8 py-4 rounded-full text-sm transition-all hover:opacity-90"
                style={{ background: "#00B7B3", fontFamily: "Sora, sans-serif", boxShadow: "0 6px 20px rgba(0,183,179,0.35)" }}
                aria-label="Start a free medical fundraiser on DonatePe"
              >
                START A FREE FUNDRAISER
              </button>
              <button
                onClick={handleGetCallback}
                className="font-bold border-2 px-8 py-4 rounded-full text-sm transition-all hover:opacity-90"
                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", fontFamily: "Sora, sans-serif" }}
                aria-label="Request a callback from DonatePe support team"
              >
                GET A CALLBACK
              </button>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}